import axios from "axios";
import { useCallback, useMemo } from "react";
import { atom, selector, selectorFamily, useRecoilRefresher_UNSTABLE, useRecoilValue, waitForAll } from "recoil";
import { v4 as uuidv4 } from 'uuid';
import { getCustomer } from "../Authorization";
import { COMMUNICATE_ERROR_MESSAGE_AIRCRAFTGROUP_LIST, COMMUNICATE_ERROR_MESSAGE_AIRCRAFT_AIRCRAFTGROUP_LIST, COMMUNICATE_ERROR_MESSAGE_AIRCRAFT_LIST, COMMUNICATE_ERROR_MESSAGE_LINKABLE_SOURCE_LIST, COMMUNICATE_ERROR_MESSAGE_SHARED_DESTINATION_LIST, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_USERGROUP_AIRCRAFTGROUP_LIST, COMMUNICATE_ERROR_MESSAGE_USERGROUP_LIST } from "../constants/ErrorMessage";
import { userState } from "./UserState";

// const DEBUG_CUSTOMER = 'WNI0001';
// const DEBUG_CUSTOMER = 'NNK0000000';
// const DEBUG_CUSTOMER = 'NEX0000000';
const DEBUG_CUSTOMER = null;

// API
// Aircraft
const _registerAircraft = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft/register`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _updateAircraft = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft/update`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listAircraft = async () => {
	const customer = DEBUG_CUSTOMER || getCustomer();
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/aircraft_linkage/aircraft/list`,
		headers: {
			'x-api-key': apiKey
		},
		params: {
			'customer_code': customer
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_AIRCRAFT_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list.sort((a, b) => a.aircraft_code.localeCompare(b.aircraft_code));
		})
		.catch(() => {
			return [];
		});
};

const _listAircraftByUserGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/links/aircraft_aircraftgroup_list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_AIRCRAFT_AIRCRAFTGROUP_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list.sort((a, b) => a.aircraft_code.localeCompare(b.aircraft_code));
		})
		.catch(() => {
			return [];
		});
};

const _deleteAircraft = async (data) => {
	// const customer = DEBUG_CUSTOMER || getCustomer();
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft/delete`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _registerAircraftImage = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft/image/register`,
		headers: {
			'x-api-key': apiKey,
			'Content-Type': 'image/png',
		},
		params: {
			'aircraft_code': data.image_aircraft_code,
			'update_time': data.update_time,
			'update_by_user': data.update_by_user,
			'api_exec_user': data.api_exec_user,
		},
		data: data.aircraft_image_blob,
	};
	
	// await axios(config)
	// await fetch(config.url, { ...config, body: config.data })
	// axiasのblobのPOSTはこの書式でしか動かない
	return await axios.post(config.url, config.data, { headers: config.headers, params: config.params })
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _deleteAircraftImage = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft/image/delete`,
		headers: { 
			'x-api-key': apiKey
		},
		params: {
			'aircraft_code': data.image_aircraft_code,
			'update_time': data.update_time,
			'update_by_user': data.update_by_user,
			'api_exec_user': data.api_exec_user,
		},
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

// AircraftGroup
const _registerAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup/register`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _updateAircraftGroup = async (data) => {
	// const customer = DEBUG_CUSTOMER || getCustomer();
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup/update`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listAircraftGroup = async () => {
	const customer = DEBUG_CUSTOMER || getCustomer();
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		// url: `${apiUrl}/aircraft_linkage/aircraftgroup/list`,
		url: `${apiUrl}/links/aircraftgroup_list`,
		headers: {
			'x-api-key': apiKey
		},
		params: {
			'customer_code': customer
		},
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_AIRCRAFTGROUP_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}		
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list;
		})
		.catch(() => {
			return [];
		});
};

// eslint-disable-next-line no-unused-vars
const _deleteAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup/delete`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listAircraftGroupSharedDestination = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_destination/list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list;
		});
};

// eslint-disable-next-line no-unused-vars
const _registerAircraftGroupSharedDestination = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_destination/register`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listSharedDestinationAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup/shared_destination_list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_SHARED_DESTINATION_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		})
		.catch(() => {
			return [];
		});
};

// eslint-disable-next-line no-unused-vars
const _deleteAircraftGroupSharedDestination = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_destination/delete`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

// eslint-disable-next-line no-unused-vars
const _registerAircraftGroupSharedAgreement = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_agreement/register`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listAircraftGroupSharedAgreement = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_agreement/list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

// eslint-disable-next-line no-unused-vars
const _deleteAircraftGroupSharedAgreement = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraftgroup_shared_agreement/delete`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};


// Aircraft-AircraftGroup
// const _listAircraftAircraftGroup = async (data) => {
// 	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
// 	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
// 	const config = {
// 		method: 'GET',
// 		url: `${apiUrl}/aircraft_linkage/aircraft_aircraftgroup/list`,
// 		headers: {
// 			'x-api-key': apiKey
// 		},
// 		params: data,
// 	};
	
// 	return await axios(config)
// 		.then(response => {
// 			const res = response.data;
// 			return res;
// 		});
// };

const _bulkupdateAircraftAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/aircraft_linkage/aircraft_aircraftgroup/bulk_update`,
		headers: {
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

// UserGroup
const _listUserGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_APP_FOSTERLINKS_API_URL;
	const apiKey = process.env.REACT_APP_APP_FOSTERLINKS_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/app_fosterlinks/usergroup/list`,
		headers: { 
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_USERGROUP_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list.sort((a, b) => a.usergroup_name.localeCompare(b.usergroup_name));
		})
		.catch(() => {
			return [];
		});
};


// AircraftGroup-UserGroup
const _registerUserGroupAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_APP_FOSTERLINKS_API_URL;
	const apiKey = process.env.REACT_APP_APP_FOSTERLINKS_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/app_fosterlinks/usergroup_aircraftgroup/register`,
		headers: { 
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listUserGroupAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_APP_FOSTERLINKS_API_URL;
	const apiKey = process.env.REACT_APP_APP_FOSTERLINKS_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/app_fosterlinks/usergroup_aircraftgroup/list`,
		headers: { 
			'x-api-key': apiKey
		},
		params: data,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_USERGROUP_AIRCRAFTGROUP_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data;
			return list;
		})
		.catch(() => {
			return [];
		});
};

const _deleteUserGroupAircraftGroup = async (data) => {
	const apiUrl = process.env.REACT_APP_APP_FOSTERLINKS_API_URL;
	const apiKey = process.env.REACT_APP_APP_FOSTERLINKS_API_KEY;
	const config = {
		method: 'POST',
		url: `${apiUrl}/app_fosterlinks/usergroup_aircraftgroup/delete`,
		headers: { 
			'x-api-key': apiKey
		},
		data: data,
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		});
};

const _listDependency = async (data) => {
	const apiUrl = process.env.REACT_APP_COMMON_API_URL;
	const apiKey = process.env.REACT_APP_COMMON_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/dependency_list`,
		headers: {
			'x-api-key': apiKey
		},
		params: data,
	};
	
	return await axios(config)
		.then(response => {
			const list = response.data || [];
			return list;
		});
};

const _listLinkableDataSource = async (params) => {
	const apiUrl = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_URL;
	const apiKey = process.env.REACT_APP_AIRCRAFT_LINKAGE_API_KEY;
	const config = {
		method: 'GET',
		url: `${apiUrl}/links/linkable_source`,
		headers: {
			'x-api-key': apiKey
		},
		params: params,
		errorHandle: {
			message: COMMUNICATE_ERROR_MESSAGE_LINKABLE_SOURCE_LIST,
			additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
		}
	};
	
	return await axios(config)
		.then(response => {
			const res = response.data;
			return res;
		})
		.catch(() => {
			return [];
		});
};


/* 機体一覧state */
const aircraftListState = atom({
	key: 'aircraftListState',
	default: selector({
		key: 'aircraftListStateSelector',
		get: _listAircraft,
	})
});

/* 機体グループ一覧state */
const aircraftGroupListState = atom({
	key: 'aircraftGroupListState',
	default: selector({
		key: 'aircraftGroupListStateSelector',
		get: _listAircraftGroup
	})
});

/* 機体グループDestination一覧state */
const aircraftGroupDestinationListState = selectorFamily({
	key: 'aircraftGroupDestinationListState',
	get: data => () => _listSharedDestinationAircraftGroup(data),
});

/* ユーザーグループに紐付く機体一覧state */
const aircraftAircraftGroupListState = selectorFamily({
	key: 'aircraftAircraftGroupListState',
	get: data => () => _listAircraftByUserGroup(data),
});

/* 機体グループに紐付く有効ユーザーグループ一覧state */
const aircraftGroupOfUserGroupListState = selectorFamily({
	kye: 'aircraftGroupOfUserGroupListState',
	get: data => () => _listUserGroupAircraftGroup(data),
});

/* 指定した全ての機体グループに紐付く有効ユーザーグループ一覧state */
const aircraftGroupOfUserGroupsState = selectorFamily({
	key: 'aircraftGroupOfUserGroupsState',
	get: (params) => ({get}) => {
		return get(waitForAll(params.map(({contract_id, aircraftgroup_id}) => aircraftGroupOfUserGroupListState({contract_id, aircraftgroup_id}))))
			.reduce((a, v, i) => {
				a[params[i].aircraftgroup_id] = Object.fromEntries(v.map(group => [group.usergroup_id, group]));
				return a;
			}, {});
	}
});

/* ユーザーグループ一覧state */
const userGroupListState = selectorFamily({
	key: 'userGroupListState',
	get: (data) => () => _listUserGroup(data),
});

const linkableSourceListState = selector({
	key: 'linkableSourceListState',
	get: () => _listLinkableDataSource(),
});

const linkableSourceDetailListState = selector({
	key: 'linkableSourceDetailListState',
	get: () => _listLinkableDataSource({
		element: 'aircraft_code',
	}),
});


/* 機体のフィルターワード */
export const aircraftListFilterWordState = atom({
	key: 'aircraftListFilterWordState',
	default: ''
});

/* 機体グループのフィルターワード */
export const aircraftGroupListFilterWordState = atom({
	key: 'aircraftGroupListFilterWordState',
	default: ''
});

/* 機体グループ(連携)のフィルターワード */
export const aircraftGroupExternalListFilterWordState = atom({
	key: 'aircraftGroupExternalListFilterWordState',
	default: ''
});

/* 選択された機体グループの識別番号 */
export const aircraftGroupSelectedIdState = atom({
	key: 'aircraftGroupSelectedIdState',
	default: null
});

/* 選択された機体グループ(連携)の識別番号 */
export const aircraftGroupExternalSelectedIdState = atom({
	key: 'aircraftGroupExternalSelectedIdState',
	default: null
});

/* 選択されたユーザーグループの識別番号 */
export const userGroupSelectedIdState = atom({
	key: 'userGroupSelectedIdState',
	default: null
});

export const AircraftDependencyType = Object.freeze({
	LINKAGE_OTHER: 0,
	LINKAGE_AIRCRAFT_AIRCRAFTGROUP: 1,
	LINKAGE_USER_GROUP_AIRCRAFTGROUP: 2,
	LINKAGE_AIRCRAFT_COPILOT: 3,
	LINKAGE_AIRCRAFT_FOSTERGA: 4,
	LINKAGE_AIRCRAFT_EXTERNAL_SOURCE: 5,
});

const parseDependency = res => {
	const ret = new Set();
	const excludes = ['master_aircraftgroup_shared_destination', 'master_aircraftgroup_shared_agreement'];
	const known = {
		'relation_aircraft_aircraftgroup': AircraftDependencyType.LINKAGE_AIRCRAFT_AIRCRAFTGROUP,
		'relation_usergroup_aircraftgroup': AircraftDependencyType.LINKAGE_USER_GROUP_AIRCRAFTGROUP,
		'relation_aircraft_copilot': AircraftDependencyType.LINKAGE_AIRCRAFT_COPILOT,
		'master_aircraft_fosterga_user': AircraftDependencyType.LINKAGE_AIRCRAFT_FOSTERGA,
		'relation_aircraft_ext': AircraftDependencyType.LINKAGE_AIRCRAFT_EXTERNAL_SOURCE,
	}
	res.forEach(v => {
		if (!(excludes.includes(v.table) && v.number_of_records === 1)) {
			ret.add(known[v.table] || AircraftDependencyType.LINKAGE_OTHER);
		}
	});
	return ret;
}

export const useAircraftList = () => {
	const aircraftList = useRecoilValue(aircraftListState);

	const ref1 = useRecoilRefresher_UNSTABLE(aircraftListState);

	const refresher = useCallback(() => {
		ref1();
	}, [ref1]);

	const checkDependency = useCallback(data => {
		const require = [
			'column_name',
			'value',
		];

		if(!require.every(key => key in data)) {
			throw new Error('Unset require parameter');
		};

		const _data = Object.fromEntries(require.map(key => ([key, data[key]])))

		return _listDependency(_data)
			.then(res => {
				return Promise.resolve(parseDependency(res));
			})
		;
	}, []);

	return {
		aircraftList,

		checkDependency,
		refreshAircraftList: refresher,
	};
}

export const useAircraftListEdit = () => {
	const {
		aircraftList,
		checkDependency,
		refreshAircraftList: refresher,
	} = useAircraftList();
	const user = useRecoilValue(userState);
	const _linkableSourceDetailList = useRecoilValue(linkableSourceDetailListState);

	const linkableSourceDetailList = useMemo(() => {
		return _linkableSourceDetailList.filter(v => v.data_source_id !== 'ext0');
	}, [_linkableSourceDetailList]);

	const registerAircraft = useCallback(data => {
		const require = [
			'aircraft_code',
			'aircraft_name',
			'operation_site',
			'customer_code',
			'api_exec_user',
		];

		const _data = Object.assign({}, data, {
			customer_code: DEBUG_CUSTOMER || user.customer,
			api_exec_user: user.userId,
			// aircraft_image: null,
			// aircraft_icon_no: null,
		});

		if(!require.every(key => key in _data)) {
			throw new Error('Unset require parameter');
		};
		return _registerAircraft(_data)
			.then(async (res) => {
				if (res.update_time) {
					return res;
				} else {
					const list = await _listAircraft();
					return list.find(v => v.aircraft_code === _data.aircraft_code);
				}
			})
			.then(res => {
				if (_data.aircraft_image_blob) {
					Object.assign(_data, {
						update_time: res?.update_time,
						update_by_user: res?.update_by_user,
					});
					return _registerAircraftImage(_data);
				}
			})
			.then(refresher);
	}, [refresher, user]);

	const updateAircraft = useCallback((lockObj, data, imageData) => {
		const aircraftCode = lockObj.aircraft_code;
		const promise = (() => {
			if (data) {
				return _updateAircraft({
					...data,
					...lockObj,
					api_exec_user: user.userId
				});
			} else {
				const res = {
					...lockObj,
					api_exec_user: user.userId
				};
				return Promise.resolve(res);
			}
		})();
		return promise
			.then(async (res) => {
				if (res.update_time) {
					return res;
				} else {
					const list = await _listAircraft();
					return list.find(v => v.aircraft_code === aircraftCode);
				}
			})
			.then(async (res) => {
				if (imageData?.delete) {
					const _data = Object.assign({}, imageData.delete, {
						update_time: res.update_time,
						update_by_user: res.update_by_user,
						api_exec_user: user.userId
					});
					return await _deleteAircraftImage(_data);
				} else {
					return res;
				}
			})
			.then(async (res) => {
				if (res.update_time) {
					return res;
				} else {
					const list = await _listAircraft();
					return list.find(v => v.aircraft_code === aircraftCode);
				}
			})
			.then(async (res) => {
				if (imageData?.register) {
					const _data = Object.assign({}, imageData.register, {
						update_time: res.update_time,
						update_by_user: res.update_by_user,
						api_exec_user: user.userId
					});
					return await _registerAircraftImage(_data);
				}
			})
			.then(refresher);
	}, [refresher, user]);

	const deleteAircraft = useCallback(data => {
		const require = [
			'aircraft_code',
		];

		const _data = Object.assign({}, data, {
			api_exec_user: user.userId,
		});

		if (!require.every(key => key in _data)) {
			throw new Error('Unset require parameter');
		};

		let promise;
		if (_data.image_aircraft_code) {
			promise = _deleteAircraftImage(_data)
				.then(async (res) => {
					if (res.update_time) {
						return res;
					} else {
						const list = await _listAircraft();
						return list.find(v => v.aircraft_code === _data.aircraft_code);
					}
				});
		} else {
			promise = Promise.resolve();
		}

		return promise
			.then((res) => {
				if (res) {
					Object.assign(_data, {
						update_time: res.update_time,
						update_by_user: res.update_by_user,
					});
				}
				return _deleteAircraft(_data)
			})
			.then(() => {
				refresher()
			});
	}, [refresher, user]);

	return {
		aircraftList,
		linkableSourceDetailList,

		registerAircraft,
		updateAircraft,
		checkDependency,
		deleteAircraft,
		refreshAircraftList: refresher,
	}
};

export const useAircraftGroupList = () => {
	const user = useRecoilValue(userState);
	const aircraftGroupList = useRecoilValue(aircraftGroupListState);

	const ref1 = useRecoilRefresher_UNSTABLE(aircraftGroupListState);

	const refresher = useCallback(() => {
		ref1();
	}, [ref1]);

	const registerAircraftGroup = useCallback(data => {
		const require = [
			'aircraftgroup_id',
			'aircraftgroup_name',
			'aircraftgroup_type',
			'customer_code',
			'api_exec_user',
		];

		const _data = Object.assign({}, data, {
			customer_code: DEBUG_CUSTOMER || user.customer,
			api_exec_user: user.userId,
			aircraftgroup_type: 0,
			aircraftgroup_id: uuidv4(),
		});

		if(!require.every(key => key in _data)) {
			throw new Error('Unset require parameter');
		};

		return _registerAircraftGroup(_data)
			.then(refresher);
	}, [refresher, user]);

	const updateAircraftGroup = useCallback((lockObj, data) => {
		const _data = Object.assign({}, data, lockObj, {
			api_exec_user: user.userId,
		});

		return _updateAircraftGroup(_data)
			.then(refresher);
	}, [refresher, user]);

	const deleteAircraftGroup = useCallback(data => {
		const require = [
			'aircraftgroup_id',
			'update_time',
			'update_by_user',
		];

		if(!require.every(key => key in data)) {
			throw new Error('Unset require parameter');
		};

		const _data = Object.assign(
			...require.map(key => ({[key]: data[key]})),
			{
				api_exec_user: user.userId,
			}
		);

		const _data2 = {
			aircraftgroup_id: _data.aircraftgroup_id
		};

		return Promise.all([
			_listAircraftGroupSharedDestination(_data2),
			_listAircraftGroupSharedAgreement(_data2),
		])
			.then(([destination, agreement]) => {
				if (destination.length >= 2 || agreement.length >=2) {
					const e = new Error();
					e.response = {
						status: 490,
						"message": "Still linked to system/aircraft",
					};
					throw e;
				}
				const keys = [
					'customer_code',
					'update_time',
					'update_by_user',
				];
				_data.shared_destination = destination.map(v => Object.fromEntries(keys.map(key => ([key, v[key]]))));
				_data.shared_agreement = agreement.map(v => Object.fromEntries(keys.map(key => ([key, v[key]]))));
				_data.usergroup_aircraftgroup = [];
			})
			.then(() => _deleteAircraftGroup(_data))
			.then(refresher);
	}, [refresher, user]);

	const checkDependency = useCallback(data => {
		const require = [
			'column_name',
			'value',
		];

		if(!require.every(key => key in data)) {
			throw new Error('Unset require parameter');
		};

		const _data = Object.fromEntries(require.map(key => ([key, data[key]])))

		return _listDependency(_data)
			.then(res => {
				return Promise.resolve(parseDependency(res));
			})
		;
	}, []);

	return {
		aircraftGroupList,

		registerAircraftGroup,
		updateAircraftGroup,
		checkDependency,
		deleteAircraftGroup,
		refreshAircraftGroupList: refresher,
	};
};

export const useAircraftGroupLinkageAircraft = () => {
	const user = useRecoilValue(userState);
	const params = { customer_code: user.customer, usergroup_id: user.group };
	const _aircraftList = useRecoilValue(aircraftAircraftGroupListState(params));
	const _aircraftGroupList = useRecoilValue(aircraftGroupListState);

	const aircraftGroupList = useMemo(() => {
		return _aircraftGroupList.filter(v => v.aircraftgroup_type === 0).sort((a, b) => a.aircraftgroup_name.localeCompare(b.aircraftgroup_name));
	}, [_aircraftGroupList]);

	const aircraftList = useMemo(() => {
		return _aircraftList.filter(v => v.customer_code === user.customer);
	}, [_aircraftList, user]);

	const aircraftOfAircraftGroupsList = useMemo(() => {
		return Object.fromEntries(aircraftGroupList.map(group => {
			const aircraftGroupId = group.aircraftgroup_id;
			return [aircraftGroupId, Object.fromEntries(_aircraftList.map(aircraft => {
				const find = aircraft.linked_aircraftgroup_id_list.find(v => v.aircraftgroup_id === aircraftGroupId);
				const isvalid = !!find;
				const prop = find || {};
				return [aircraft.aircraft_code, { ...aircraft, isvalid, ...prop }];
			}))];
		}))
	}, [_aircraftList, aircraftGroupList]);

	const ref1 = useRecoilRefresher_UNSTABLE(aircraftAircraftGroupListState(params));
	const ref2 = useRecoilRefresher_UNSTABLE(aircraftGroupListState);

	const refresher = useCallback(() => {
		ref1();
		ref2();
	}, [ref1, ref2]);

	const updateAircraftGroupLinkageAircraft = useCallback(data => {
		const require = [
			'aircraft_code',
			'aircraftgroup_id',
			'customer_code',
			'api_exec_user',
		];

		const _data = data.map(v => Object.assign({}, v, {
			customer_code: DEBUG_CUSTOMER || user.customer,
			api_exec_user: user.userId,
		}));

		if(_data.some(d => !require.every(key => key in d))) {
			throw new Error('Unset require parameter');
		};
		return _bulkupdateAircraftAircraftGroup(_data)
			.then(refresher);
	}, [refresher, user]);

	return {
		aircraftList,
		aircraftGroupList,
		aircraftOfAircraftGroupsList,

		updateAircraftGroupLinkageAircraft,
		refresher,
	};
};

export const useAircraftGroupExternalLinkageAircraft = () => {
	const user = useRecoilValue(userState);
	const params = { customer_code: user.customer, usergroup_id: user.group };
	const _aircraftList = useRecoilValue(aircraftAircraftGroupListState(params));
	const _aircraftGroupList = useRecoilValue(aircraftGroupListState);

	const aircraftGroupList = useMemo(() => {
		return _aircraftGroupList
			.filter(v => [1,2].includes(v.aircraftgroup_type))
			.sort((a, b) => a.aircraftgroup_type - b.aircraftgroup_type || a.aircraftgroup_name.localeCompare(b.aircraftgroup_name));
	}, [_aircraftGroupList]);

	const aircraftOfAircraftGroupsList = useMemo(() => {
		return Object.fromEntries(aircraftGroupList.map(group => {
			const aircraftGroupId = group.aircraftgroup_id;
			return [aircraftGroupId, Object.fromEntries(_aircraftList.map(aircraft => {
				const find = aircraft.linked_aircraftgroup_id_list.find(v => v.aircraftgroup_id === aircraftGroupId);
				const isvalid = !!find;
				const prop = find || {};
				return [aircraft.aircraft_code, { ...aircraft, isvalid, ...prop }];
			}))];
		}))
	}, [_aircraftList, aircraftGroupList]);

	const aircraftList = useMemo(() => {
		const compareCustomer = (a, b) => {
			if (a.customer_code === user.customer && b.customer_code === user.customer) {
				return 0;
			} else if (a.customer_code === user.customer) {
				return -1;
			} else if (b.customer_code === user.customer) {
				return 1;
			} else {
				return a.customer_code.localeCompare(b.customer_code);
			}
		}
		return _aircraftList
			.slice()
			.sort((a, b) => {
				return compareCustomer(a, b) || a.aircraft_code.localeCompare(b.aircraft_code);
			})
		;
	}, [_aircraftList, user])

	const ref1 = useRecoilRefresher_UNSTABLE(aircraftAircraftGroupListState(params));
	const ref2 = useRecoilRefresher_UNSTABLE(aircraftGroupListState);

	const refresher = useCallback(() => {
		ref1();
		ref2();
	}, [ref1, ref2]);

	const updateAircraftGroupLinkageAircraft = useCallback(data => {
		const require = [
			'aircraft_code',
			'aircraftgroup_id',
			'customer_code',
			'api_exec_user',
		];

		const _data = data.map(v => Object.assign({}, v, {
			customer_code: DEBUG_CUSTOMER || user.customer,
			api_exec_user: user.userId,
		}));

		if(_data.some(d => !require.every(key => key in d))) {
			throw new Error('Unset require parameter');
		};
		return _bulkupdateAircraftAircraftGroup(_data)
			.then(refresher);
	}, [refresher, user]);

	return {
		aircraftList,
		aircraftGroupList,
		aircraftOfAircraftGroupsList,

		updateAircraftGroupLinkageAircraft,
		refresher,
	};
};

export const useAircraftGroupLinkageUserGroup = () => {
	const user = useRecoilValue(userState);
	const contractParams = { contract_id : user.contract };
	const customerParams = { customer_code: user.customer };
	const userGroupList = useRecoilValue(userGroupListState(contractParams));
	const _aircraftGroupList = useRecoilValue(aircraftGroupDestinationListState(customerParams));

	const aircraftGroupList = useMemo(() => {
		return _aircraftGroupList
			// .filter(v => v.customer_code === user.customer)
			.slice()
			.sort((a, b) => a.aircraftgroup_type - b.aircraftgroup_type || a.aircraftgroup_name.localeCompare(b.aircraftgroup_name));
	}, [_aircraftGroupList]);

	const groupParams = aircraftGroupList.map(group => ({ contract_id : user.contract, aircraftgroup_id: group.aircraftgroup_id }));
	const aircraftGroupOfUserGroupsList = useRecoilValue(aircraftGroupOfUserGroupsState(groupParams));

	const ref1 = useRecoilRefresher_UNSTABLE(userGroupListState(contractParams));
	const ref2 = useRecoilRefresher_UNSTABLE(aircraftGroupDestinationListState(customerParams));
	const ref3 = useRecoilRefresher_UNSTABLE(aircraftGroupOfUserGroupsState(groupParams));

	const refresher = useCallback(() => {
		ref1();
		ref2();
		ref3();
	}, [ref1, ref2, ref3]);

	const updateAircraftGroupLinkageUserGroup = useCallback(data => {
		const require = [
			'usergroup_id',
			'aircraftgroup_id',
			'customer_code',
			'api_exec_user',
			'contract_id',
		];

		const _data = data.map(v => Object.assign({}, v, {
			customer_code: DEBUG_CUSTOMER || user.customer,
			api_exec_user: user.userId,
		}));

		if(_data.some(d => !require.every(key => key in d))) {
			throw new Error('Unset require parameter');
		};

		const [registerData, deleteData] = _data.reduce((a, v) => {
			const [r, d] = a;
			if (v.update_time === null) {
				r.push(v);
			} else {
				d.push(v);
			}
			return a;
		}, [[], []]);

		return Promise.resolve()
			.then(() => {
				if (registerData.length === 0) {
					return Promise.resolve();
				}
				return _registerUserGroupAircraftGroup(registerData);
			})
			.then(() => {
				if (deleteData.length === 0) {
					return Promise.resolve();
				}
				return _deleteUserGroupAircraftGroup(deleteData);
			})
			.then(refresher);
	}, [refresher, user]);

	return {
		aircraftGroupList,
		userGroupList,
		aircraftGroupOfUserGroupsList,

		updateAircraftGroupLinkageUserGroup,
		refresher,
	};
};

export const useLinkableSourceList = (filter) => {
	const _linkableSourceList = useRecoilValue(linkableSourceListState);

	const linkableSourceList = useMemo(() => {
		return _linkableSourceList.filter(v => !filter || v.data_source_id !== 'ext0');
	}, [filter, _linkableSourceList]);

	return {
		linkableSourceList,
	};
};
